import React from 'react';
import {
  IconCenter,
  IconHome,
  IconInfoRoundFilled,
  IconVideoCamera,
} from '@maiia/design-system';
import {
  FormControl,
  Select,
  MenuItem,
  SelectProps,
  Alert,
  Box,
  Typography,
  ListSubheader,
  Stack,
} from '@mui/material';
import { FieldProps } from 'formik';
import { ConsultationReasonHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { styled, css } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import dayjs from 'dayjs';
import { MenuListProps } from '@mui/material/MenuList/MenuList';
import { Link } from '@kiiwi/ui';
import Text from '../../atoms/Text/Text';
import { useTranslation } from '@/src/i18n';
import {
  PHYSICAL,
  TELECONSULTATION,
  PHYSICAL_OR_TELECONSULTATION,
  HOME_VISIT,
  PATIENT_LIMITATION_WARNING_ZENDESK,
} from '@/src/constants';

interface ExtendedSelectDisplayProps
  extends React.HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

interface ExtendedMenuListProps extends MenuListProps {
  'data-testid'?: string;
}

type Props = {
  options: ConsultationReasonHcd[];
  valueProperty: string;
  defaultLabel: string;
  optionRender: (entity: ConsultationReasonHcd) => string;
  showType: any;
} & FieldProps &
  SelectProps;

const iconsByTypeSeparator = ' / ';
const iconsByType = {
  [PHYSICAL]: [<IconCenter key="center" />],
  [TELECONSULTATION]: [<IconVideoCamera key="videocam" />],
  [PHYSICAL_OR_TELECONSULTATION]: [
    <IconCenter key="center" />,
    iconsByTypeSeparator,
    <IconVideoCamera key="videocam" />,
  ],
  [HOME_VISIT]: [<IconHome key="home-visit" />],
};

const ONE_WEEK_HOURS = 7 * 24;

const MenuItemLabelContainer = styled(Box)(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    .icon-type {
      display: flex;
      justify-content: flex-end;
      flex-shrink: 0; // Make sure icon don't get shrinked
      color: ${theme.palette.info.light};
      svg {
        width: auto;
        height: ${theme.spacing(2)};
      }
    }
  `,
);

type ConsultationMenuItemContentProps = {
  reason: ConsultationReasonHcd;
  optionRender: (consultationReasonHcd: ConsultationReasonHcd) => string;
  showType: boolean;
  showNextVisibleAvailability?: boolean;
};

// Custom MenuItem can be tricky to implement (https://stackoverflow.com/a/66948157)
// So here we implement the content only
const ConsultationMenuItemContent = ({
  reason,
  optionRender,
  showType,
  showNextVisibleAvailability = false,
}: ConsultationMenuItemContentProps) => {
  const { t } = useTranslation();
  const optionRenderValue = optionRender(reason);
  const hasLimitation = reason.patientLimitation === 'CENTER';
  const nextVisibleAvailability =
    showNextVisibleAvailability &&
    reason.firstAvailabilityVisibilityStartDateTime
      ? dayjs(reason.firstAvailabilityVisibilityStartDateTime).format(
          'DD/MM/YYYY HH[h]mm',
        )
      : undefined;

  const consultationTypeIcons = React.useMemo(() => {
    if (!reason.availableTypes) {
      return;
    }

    const result: (string | JSX.Element)[] = [];
    reason.availableTypes.forEach((type, index, arr) => {
      result.push(...iconsByType[type]);
      if (arr.length - 1 > index) {
        result.push(iconsByTypeSeparator);
      }
    });

    return result;
  }, [reason.availableTypes]);

  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      <MenuItemLabelContainer>
        <Typography component="span">
          {`${optionRenderValue || ''}${hasLimitation ? '*' : ''}`}
        </Typography>
        {showType && consultationTypeIcons && (
          <Typography component="span" className="icon-type">
            {consultationTypeIcons}
          </Typography>
        )}
      </MenuItemLabelContainer>
      {nextVisibleAvailability && (
        <Box>
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: '600' }}
          >
            {t('next-visible-availability-label', {
              value: nextVisibleAvailability,
            })}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const SelectConsultationReason = ({
  field,
  options,
  valueProperty,
  defaultLabel,
  optionRender = ({ name }) => name || '',
  showType,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const [showPlaceholder, setShowPlaceholder] = React.useState(true);
  const { onChange: onChangeField, value, ...restFromField } = field;
  const { onChange: onChangeProps, ...restFromProps } = props;
  const selectedValue = options?.find(
    option => option[valueProperty] === value,
  );

  const handleChange = event => {
    onChangeField(event);
    if (onChangeProps) onChangeProps(event, null);
  };

  const hasPatientLimitation = options
    .map(o => o.patientLimitation)
    .some(limitation => limitation === 'CENTER');

  const onlineOpening = selectedValue?.allowAvailabilitiesXHoursBefore;

  const onlineAvailability = React.useMemo(() => {
    if (!onlineOpening) return null;

    if (onlineOpening < ONE_WEEK_HOURS) {
      return `${onlineOpening}h`;
    }
    if (onlineOpening === ONE_WEEK_HOURS) {
      return t('1-week');
    }

    return null;
  }, [onlineOpening, t]);

  const [
    availableReasons,
    notAvailableReasons,
    proReasons,
  ] = React.useMemo(() => {
    return options.reduce(
      (acc, curr) => {
        // We don't display unavailable reasons.
        if (curr.isAvailable === false) {
          return acc;
        }

        // 0 = array with available reasons
        // 1 = array with not available reasons for now but with a visibility date
        // 2 = array with available reasons for PRO only
        let accIndexToPush = 0;

        if (curr.allowAvailabilitiesXHoursBefore) {
          const firstAvailabilityVisibilityDate = dayjs(
            curr.firstAvailabilityVisibilityStartDateTime,
          );
          const today = dayjs();

          if (!today.isAfter(firstAvailabilityVisibilityDate)) {
            accIndexToPush = 1;
          }
        }

        // TODO: maybe remove accIndexToPush === 0 check ?
        if (accIndexToPush === 0 && curr.isVisibleForProOnly) {
          // Available - PRO only
          accIndexToPush = 2;
        }

        acc[accIndexToPush].push(curr);
        return acc;
      },
      [
        [] as ConsultationReasonHcd[],
        [] as ConsultationReasonHcd[],
        [] as ConsultationReasonHcd[],
      ],
    );
  }, [options]);

  const menuItemProps = (reason: ConsultationReasonHcd) => {
    return {
      value: reason[valueProperty],
      'data-cy': optionRender(reason),
      'aria-label': `${optionRender(reason)}${
        reason.patientLimitation === 'CENTER'
          ? `- ${t('reason-limited-to-known-patient')}`
          : ''
      }`,
    };
  };

  return (
    <FormControl className="select-wrapper" sx={{ width: '100%' }}>
      <Select
        displayEmpty
        value={selectedValue?.[valueProperty] || ''}
        onChange={handleChange}
        onOpen={() => setShowPlaceholder(false)}
        onClose={() => setShowPlaceholder(true)}
        variant="standard"
        sx={{
          '& .MuiSelect-select span:first-of-type': {
            // This style will be applied on the selected item when the select input is closed
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
        }}
        SelectDisplayProps={
          {
            'data-testid': 'select-consultation-reason',
          } as ExtendedSelectDisplayProps
        }
        MenuProps={{
          MenuListProps: {
            'data-testid': 'consultation-reason-menu',
          } as ExtendedMenuListProps,
        }}
        {...restFromField}
        {...restFromProps}
      >
        {showPlaceholder && (
          <MenuItem value="" disabled>
            <Typography component="span">{defaultLabel}</Typography>
          </MenuItem>
        )}

        {/* The Select component doesn't accept a Fragment as a child.
        Consider providing an array instead. */}
        {proReasons.length > 0 && [
          <ListSubheader key="subheader-available-pro" color="primary">
            {t('AppointmentForm__Patterns_available_pro')}
          </ListSubheader>,
          proReasons.map(reason => (
            <MenuItem {...menuItemProps(reason)} key={reason.id}>
              <ConsultationMenuItemContent
                reason={reason}
                optionRender={optionRender}
                showType={showType}
              />
            </MenuItem>
          )),
        ]}
        {proReasons.length > 0 && availableReasons.length > 0 && <Divider />}

        {availableReasons.length > 0 && [
          <ListSubheader key="subheader-available" color="primary">
            {t('AppointmentForm__Patterns_available')}
          </ListSubheader>,
          availableReasons.map(reason => {
            return (
              <MenuItem {...menuItemProps(reason)} key={reason.id}>
                <ConsultationMenuItemContent
                  reason={reason}
                  optionRender={optionRender}
                  showType={showType}
                />
              </MenuItem>
            );
          }),
        ]}

        {availableReasons.length > 0 && notAvailableReasons.length > 0 && (
          <Divider />
        )}

        {notAvailableReasons.length > 0 && [
          <ListSubheader key="subheader-unavailable">
            {t('AppointmentForm__Patterns_currently_unavailable')}
          </ListSubheader>,
          notAvailableReasons.map(reason => (
            <MenuItem {...menuItemProps(reason)} key={reason.id} disabled>
              <ConsultationMenuItemContent
                reason={reason}
                optionRender={optionRender}
                showNextVisibleAvailability
                showType={showType}
              />
            </MenuItem>
          )),
        ]}

        {hasPatientLimitation && (
          <Typography className="px-4 py-1.5 text-right italic text-neutral-disabled">
            {t('asterisk')}
            {t('reason-limited-to-known-patient')}
          </Typography>
        )}
      </Select>

      {selectedValue?.patientLimitation === 'CENTER' && (
        <Alert
          className="mt-4"
          icon={<IconInfoRoundFilled />}
          severity="warning"
        >
          {t('patient-limitation-warning')}{' '}
          <Link href={PATIENT_LIMITATION_WARNING_ZENDESK} target="_blank">
            {t('learn_more')}
          </Link>
        </Alert>
      )}

      {onlineOpening && onlineAvailability && (
        <Text variant="paragraph" className="is-right">
          <Typography component="span" className="warning">
            {t('limit_time_reason', {
              time: onlineAvailability,
            })}
          </Typography>
        </Text>
      )}
    </FormControl>
  );
};

export default SelectConsultationReason;
